import type React from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "~/components/ui/button";
import { TOAST_MESSAGES } from "~/constants/messages";
import { OrganizationApi } from "~/functions/api/organization";
import close from "~/images/close.svg";
import type {
  CustomError,
  FailedInvite,
  InviteGuest,
  InviteGuestInfo,
} from "~/interface/organization";
import type { User } from "~/interface/user";
import { addGuestAction } from "~/slices/organizationSlice";
import type { RootState } from "~/store";
import InviteGuestForm from "../inviteGuestForm";
import PermitDescriptionTooltipGuest from "../PermitDescriptionTooltipGuest";
import styles from "./index.module.scss";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InviteGuestModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const [guests, setGuests] = useState<InviteGuestInfo[]>([{ email: "" }]);
  const [error, setError] = useState(false);
  const [isLock, setIsLock] = useState(false);
  const [failedInvites, setFailedInvites] = useState<FailedInvite[]>([]);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const dispatch = useDispatch();

  const handleClose = () => {
    setFailedInvites([]);
    setError(false);
    onClose();
  };

  const getErrorMessage = (error: CustomError): string => {
    // Ensure we always return a string, not an object
    const originalError = error.details?.originalError;
    if (typeof originalError === "string" && originalError.trim()) {
      return originalError;
    }
    if (typeof error.message === "string" && error.message.trim()) {
      return error.message;
    }
    return "不明なエラー";
  };

  const validateForm = () => {
    // メールアドレスが未入力のメンバーがいるかをチェック
    return guests.every((guest) => guest.email);
  };

  const handleSubmit = async () => {
    setIsLock(true);
    if (!validateForm()) {
      setError(true);
      setIsLock(false);
      return;
    }
    setError(false);

    const organizationApi = new OrganizationApi();

    const inviteData: InviteGuest = {
      users: guests,
    };

    const response = await organizationApi.inviteGuest(currentUser.organization_id, inviteData);

    if (response.success) {
      const { successfulInvites, failedInvites } = response.data;

      // Add successful invites to Redux store
      successfulInvites.forEach((newGuest: User) => {
        dispatch(
          addGuestAction({
            id: newGuest.id,
            display_name: newGuest.display_name,
            first_name: newGuest.first_name || "",
            last_name: newGuest.last_name || "",
            role: "guest",
            organization_id: currentUser.organization_id,
            userInfo_id: newGuest.userInfo_id,
            status: "active",
          }),
        );
      });

      // Handle results based on success/failure counts
      if (successfulInvites.length > 0 && failedInvites.length === 0) {
        // All invitations successful
        toast.success(TOAST_MESSAGES.ORGANIZATION.INVITE_GUEST_SUCCESS);
        setIsLock(false);
        handleClose();
      } else if (successfulInvites.length > 0 && failedInvites.length > 0) {
        // Partial success - some failed
        toast.warning(
          `${successfulInvites.length}件の招待に成功しました。${failedInvites.length}件の招待に失敗しました。`,
        );
        setFailedInvites(failedInvites);
        setIsLock(false);
        // Don't close modal - show failed invites
      } else if (successfulInvites.length === 0 && failedInvites.length > 0) {
        // All invitations failed
        toast.error(TOAST_MESSAGES.ORGANIZATION.INVITE_GUEST_FAILED);
        setFailedInvites(failedInvites);
        setIsLock(false);
        // Don't close modal - show failed invites
      } else {
        // Unexpected case
        toast.error(TOAST_MESSAGES.ORGANIZATION.INVITE_GUEST_FAILED);
        setIsLock(false);
        handleClose();
      }
    } else {
      // エラーメッセージがAPIから伝播される
      console.error("ゲスト招待に失敗しました:", response.error);
      toast.error(response.error);
      setIsLock(false);
      handleClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles["overlay"]}>
      <div className={styles["modal"]}>
        <div className={styles["modalHeader"]}>
          <h2>ゲスト招待</h2>
          <button className={styles["closeButton"]} onClick={handleClose}>
            <img src={close} alt="閉じる" />
          </button>
        </div>

        <div className={styles["modalContent"]}>
          <p className={styles["description"]}>
            招待されたメンバーに招待メールが届くのでユーザー登録を行なってください。
          </p>

          <PermitDescriptionTooltipGuest>
            <div className={styles["infoSection"]}>
              <button className={styles["infoButton"]}>?</button>
              <span className={styles["infoText"]}>権限について</span>
            </div>
          </PermitDescriptionTooltipGuest>

          <InviteGuestForm guests={guests} setGuests={setGuests} />
          {error && <div className={styles["errorText"]}>※入力内容に誤りがあります</div>}

          {failedInvites.length > 0 && (
            <div className={styles["failedInvitesSection"]}>
              <h3 className={styles["failedInvitesTitle"]}>招待に失敗したゲスト</h3>
              <div className={styles["failedInvitesList"]}>
                {failedInvites.map((failedInvite, index) => (
                  <div key={index} className={styles["failedInviteItem"]}>
                    <div className={styles["failedInviteDetails"]}>
                      <span className={styles["failedEmail"]}>{failedInvite.email}</span>
                      <span className={styles["failedRole"]}>(ゲスト)</span>
                    </div>
                    <div className={styles["failedError"]}>
                      {getErrorMessage(failedInvite.error)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles["modalFooter"]}>
          <Button label="キャンセル" styleType="white" onClick={handleClose} />
          <Button
            label={!isLock ? "招待" : "招待中"}
            styleType="blue"
            onClick={handleSubmit}
            disabled={isLock}
            dataTestId="invite-submit"
          />
        </div>
      </div>
    </div>
  );
};

export default InviteGuestModal;
